import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import type { CalendarItem } from '../../../shared/types';
import { toLocalInputValue } from '../../lib/format';
import { cn } from '../../lib/utils';
import { addDaysToCell, dayKeyFromLocalInput, MONTH_LABELS, WEEKDAY_LABELS } from './calendarDate';
import type { DayCell } from './calendarDate';
import { CalendarItemPill } from './CalendarItemPill';

function itemTimestamp(item: CalendarItem): number {
  return item.scheduledAt ?? item.publishedAt ?? 0;
}

export interface WeekViewProps {
  /** Monday of the displayed week. */
  weekStart: DayCell;
  items: CalendarItem[];
  timezone: string;
  onSelectItem: (item: CalendarItem) => void;
  onAddDay: (dayKey: string) => void;
  className?: string;
}

/** Seven day columns, each a chronological list of pills. Owned by WEB-3. */
export function WeekView({ weekStart, items, timezone, onSelectItem, onAddDay, className }: WeekViewProps) {
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDaysToCell(weekStart.year, weekStart.month, weekStart.day, index)),
    [weekStart.year, weekStart.month, weekStart.day],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    for (const item of items) {
      const ms = itemTimestamp(item);
      if (!ms) continue;
      const key = dayKeyFromLocalInput(toLocalInputValue(ms, timezone));
      const bucket = map.get(key);
      if (bucket) bucket.push(item);
      else map.set(key, [item]);
    }
    for (const bucket of map.values()) bucket.sort((a, b) => itemTimestamp(a) - itemTimestamp(b));
    return map;
  }, [items, timezone]);

  const todayKey = useMemo(() => dayKeyFromLocalInput(toLocalInputValue(Date.now(), timezone)), [timezone]);

  return (
    <div className={cn('pap-card grid grid-cols-1 overflow-hidden sm:grid-cols-7', className)}>
      {days.map((cell, index) => {
        const dayItems = byDay.get(cell.key) ?? [];
        const isToday = cell.key === todayKey;

        return (
          <div
            key={cell.key}
            className={cn(
              'group flex min-h-[16rem] flex-col border-b border-border sm:border-b-0 sm:border-r last:sm:border-r-0',
              !isToday && 'bg-surface',
              isToday && 'bg-accent-soft/40',
            )}
          >
            <div className="flex items-center justify-between border-b border-border px-2.5 py-2">
              <div>
                <p className="text-[11px] font-medium uppercase tracking-wide text-subtle">
                  {WEEKDAY_LABELS[index]}
                </p>
                <p className={cn('pap-tnum text-sm font-semibold', isToday ? 'text-accent' : 'text-fg')}>
                  {cell.day} <span className="text-xs font-normal text-subtle">{MONTH_LABELS[cell.month - 1].slice(0, 3)}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => onAddDay(cell.key)}
                aria-label="Add a post on this day"
                className={cn(
                  'flex size-6 items-center justify-center rounded-full text-subtle opacity-0 transition-opacity',
                  'hover:bg-elevated hover:text-fg focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring',
                  'group-hover:opacity-100',
                )}
              >
                <Plus className="size-3.5" />
              </button>
            </div>
            <div className="flex-1 space-y-0.5 overflow-y-auto p-1.5">
              {dayItems.length === 0 ? (
                <p className="px-1.5 py-2 text-[11px] text-subtle">Nothing scheduled</p>
              ) : (
                dayItems.map((item) => (
                  <CalendarItemPill
                    key={item.platformPostId}
                    item={item}
                    timezone={timezone}
                    onClick={() => onSelectItem(item)}
                    showTime
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
